'use client';

import React, { useState } from 'react';
import { Camera, ImagePlus, X, ZoomIn } from 'lucide-react';

import { useEventSettings } from '@/context/EventSettingsContext';

export default function EventPhotoGallery() {
  const { settings, isLoading } = useEventSettings();
  const galleryPhotos = settings?.galleryPhotos ?? [];

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState<string>('');

  const openLightbox = (src: string, alt: string) => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
  };

  const closeLightbox = () => setLightboxSrc(null);

  // While loading, show skeleton
  if (isLoading) {
    return (
      <section className="w-full bg-gradient-to-b from-[#0a1628] to-[#0d1f3c] py-16 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-600/20 border border-red-500/30 animate-pulse" />
            <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="h-8 w-64 bg-white/10 rounded animate-pulse mb-2" />
          <div className="h-4 w-96 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[180px]">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`rounded-xl bg-white/5 animate-pulse ${i === 0 || i === 5 ? 'col-span-2' : ''}`} />
          ))}
        </div>
      </section>
    );
  }

  // If no photos uploaded yet, show placeholder grid
  if (galleryPhotos.length === 0) {
    const EMPTY_SLOTS = [
      { id: 1, span: 'wide' },
      { id: 2, span: 'tall' },
      { id: 3, span: 'normal' },
      { id: 4, span: 'normal' },
      { id: 5, span: 'normal' },
      { id: 6, span: 'wide' },
    ];
    return (
      <section className="w-full bg-gradient-to-b from-[#0a1628] to-[#0d1f3c] py-16 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-600/20 border border-red-500/30 flex items-center justify-center">
              <Camera size={16} className="text-red-400" />
            </div>
            <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Event Gallery</span>
          </div>
          <h2 className="text-white font-extrabold text-2xl sm:text-3xl leading-tight mb-2">
            National BLS Day 2026
            <span className="block text-sky-300 text-lg sm:text-xl font-bold mt-1">BLS Marathon — Moments</span>
          </h2>
          <p className="text-white/50 text-sm max-w-lg">
            Snapshots from the event — participants, training sessions, and the community coming together to learn life-saving skills.
          </p>
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[180px]">
          {EMPTY_SLOTS.map((slot) => (
            <div
              key={slot.id}
              className={`relative rounded-xl border-2 border-dashed border-white/15 bg-white/5 flex flex-col items-center justify-center gap-2 ${
                slot.span === 'wide' ? 'col-span-2' : slot.span === 'tall' ? 'row-span-2' : ''
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <ImagePlus size={18} className="text-white/30" />
              </div>
              <span className="text-white/25 text-xs font-medium text-center px-2">Photo coming soon</span>
            </div>
          ))}
        </div>
        <div className="max-w-5xl mx-auto mt-6">
          <p className="text-white/25 text-xs text-center">
            Photos will be added after the event · Managed via Admin Dashboard
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-gradient-to-b from-[#0a1628] to-[#0d1f3c] py-16 px-4 sm:px-8">
      {/* Section Header */}
      <div className="max-w-5xl mx-auto mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-red-600/20 border border-red-500/30 flex items-center justify-center">
            <Camera size={16} className="text-red-400" />
          </div>
          <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Event Gallery</span>
        </div>
        <h2 className="text-white font-extrabold text-2xl sm:text-3xl leading-tight mb-2">
          {settings.eventName}
          <span className="block text-sky-300 text-lg sm:text-xl font-bold mt-1">BLS Marathon — Moments</span>
        </h2>
        <p className="text-white/50 text-sm max-w-lg">
          Snapshots from the event — participants, training sessions, and the community coming together to learn life-saving skills.
        </p>
      </div>

      {/* Bento Gallery Grid */}
      <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[180px]">
        {galleryPhotos.map((photo, index) => {
          // Give first and every 7th photo a wide span for visual variety
          const isWide = index === 0 || index % 7 === 6;
          return (
            <div
              key={photo.id}
              className={`relative rounded-xl overflow-hidden group cursor-pointer border border-white/10 ${isWide ? 'col-span-2' : ''}`}
              onClick={() => openLightbox(photo.src, photo.alt)}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-end justify-start p-3">
                <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                {photo.caption && (
                  <span className="text-white text-xs font-medium bg-black/50 rounded px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 line-clamp-1">
                    {photo.caption}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            onClick={closeLightbox}
          >
            <X size={20} className="text-white" />
          </button>
          <div className="relative max-w-4xl max-h-[85vh] w-full h-full flex items-center justify-center">
            <img
              src={lightboxSrc}
              alt={lightboxAlt}
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </section>
  );
}
